import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Smartphone, X } from "lucide-react";
import { useAppSelector } from "../../app/hooks";
import { selectUserProfile } from "../../features/authentication/authenticationSlice";
import { detectPlatform, showsQrCodes, storesFor } from "../../features/app-stores/platform";
import { dismissGetTheAppCard, isGetTheAppCardDismissed } from "../../features/app-stores/dismissal";
import { buildBookingPageQrSvg, svgToDataUrl } from "../../features/trial-requests/utils/bookingPage";
import StoreBadge from "./StoreBadge";

/**
 * A compact Home card pointing to the Timeplanned app: the store badges for this device and, on a
 * wide computer screen, a small QR code per store. Hidden for good once dismissed; the left menu's
 * "Get the app" stays the permanent way in.
 */
const GetTheAppCard = () => {
  const { t } = useTranslation();
  const userId = useAppSelector(selectUserProfile)?.sub ?? "";
  const [dismissed, setDismissed] = useState(() => isGetTheAppCardDismissed(userId));
  const platform = useMemo(() => detectPlatform(), []);
  const stores = storesFor(platform);
  const withQr = showsQrCodes(platform);

  if (dismissed) return null;

  const dismiss = (): void => {
    dismissGetTheAppCard(userId);
    setDismissed(true);
  };

  return (
    <div className="with-border relative rounded-2xl p-4 lg:p-6" data-testid="get-the-app-card">
      <button
        type="button"
        className="absolute top-3 right-3 rounded-md p-1 text-grey-40 hover:bg-grey-95 dark:text-grey-80 dark:hover:bg-primary-20"
        aria-label={t("getTheApp.dismiss")}
        title={t("getTheApp.dismiss")}
        onClick={dismiss}
      >
        <X size={16} />
      </button>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="pr-8 lg:pr-0">
          <h3 className="flex items-center gap-2">
            <Smartphone size={20} className="text-grey-30 dark:text-grey-70" />
            {t("getTheApp.cardTitle")}
          </h3>
          <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">{t("getTheApp.cardText")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-4 lg:pr-8">
          {stores.map((store) => (
            <div key={store.id} className="flex flex-col items-center gap-2">
              {withQr && <CardQr url={store.url} alt={t("getTheApp.qrAlt", { store: t(store.nameKey) })} />}
              {/* One badge-row height for both, so the QR codes line up above them. */}
              <div className="flex h-[58px] items-center">
                <StoreBadge store={store} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/** A small QR code, only on large screens — the white chip stays in dark mode so it scans. */
const CardQr = ({ url, alt }: { url: string; alt: string }) => {
  const src = useMemo(() => svgToDataUrl(buildBookingPageQrSvg(url)), [url]);
  return (
    <img
      src={src}
      alt={alt}
      data-testid="get-the-app-card-qr"
      className="hidden h-24 w-24 rounded-lg border border-grey-90 bg-white p-1 lg:block dark:border-grey-50"
    />
  );
};

export default GetTheAppCard;
